import { authedFetch } from "@/lib/authed-fetch";
import { useState, useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { Loader2, CheckCircle, XCircle, Cloud, RefreshCw, ExternalLink, Copy, AlertTriangle, Key, Eye, EyeOff, ChevronDown, ChevronUp } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { formatDistanceToNow } from "date-fns";

interface DropboxStatus {
  connected: boolean;
  hasValidToken: boolean;
  canRefreshToken: boolean;
  lastRefresh?: string;
  error?: string;
  folderPath?: string;
  diagnosis?: string;
  recommendation?: string;
}

export function DropboxSettings() {
  const { toast } = useToast();
  const [status, setStatus] = useState<DropboxStatus | null>(null);
  const [testError, setTestError] = useState<string | null>(null);
  const [showTokenGeneration, setShowTokenGeneration] = useState(false);
  const [authCode, setAuthCode] = useState("");
  const [oauthUrl, setOauthUrl] = useState("");
  const [generatedToken, setGeneratedToken] = useState<string | null>(null);
  
  // Manual token entry state
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualAppKey, setManualAppKey] = useState('');
  const [manualAppSecret, setManualAppSecret] = useState('');
  const [manualRefreshToken, setManualRefreshToken] = useState('');
  const [showAppSecret, setShowAppSecret] = useState(false);
  const [showRefreshTokenField, setShowRefreshTokenField] = useState(false);
  const [isSavingCredentials, setIsSavingCredentials] = useState(false);

  // Credentials info query
  const { data: credentialsInfo, refetch: refetchCredentialsInfo } = useQuery<{
    database: {
      hasRefreshToken: boolean;
      hasAppKey: boolean;
      hasAppSecret: boolean;
      hasAccessToken: boolean;
      refreshTokenPreview?: string;
      appKeyPreview?: string;
      lastRefresh?: string;
    };
    environment: {
      hasRefreshToken: boolean;
      hasAppKey: boolean;
      hasAppSecret: boolean;
      appKeyPreview?: string;
    };
  }>({
    queryKey: ['/api/dropbox/credentials-info'],
    refetchOnWindowFocus: false,
    staleTime: 60000,
  });

  // Track if this is a manual test (not initial load)
  const [isManualTest, setIsManualTest] = useState(false);

  const testConnectionMutation = useMutation({
    onMutate: () => setTestError(null),
    mutationFn: async () => {
      console.time('[Dropbox Client] testConnection API call');
      const response = await apiRequest('POST', '/api/dropbox/test-detailed');
      console.timeEnd('[Dropbox Client] testConnection API call');
      return response.json();
    },
    onSuccess: (data: DropboxStatus) => {
      console.timeEnd('[Dropbox Client] Mount to status ready');
      console.log('[Dropbox Client] Status received:', data.connected ? 'Connected' : 'Not connected');
      setStatus(data);
      // Only show toast for manual tests (not initial load)
      if (isManualTest) {
        toast({
          title: data.connected ? "Connection Successful" : "Connection Failed",
          description: data.connected 
            ? `Connected to Dropbox. Folder: ${data.folderPath}` 
            : data.error || "Unable to connect to Dropbox",
          variant: data.connected ? "default" : "destructive",
        });
        setIsManualTest(false);
      }
    },
    onError: (error) => {
      if (isManualTest) {
        toast({
          title: "Test Failed",
          description: error instanceof Error ? error.message : "Unable to run the Dropbox check",
          variant: "destructive",
        });
        setIsManualTest(false);
      }
      // An app/session/network error says nothing about Dropbox credentials.
      setTestError(error instanceof Error ? error.message : "Unable to run the Dropbox check");
    },
  });

  // Load status on component mount (silent - no toast)
  useEffect(() => {
    console.time('[Dropbox Client] Mount to status ready');
    console.log('[Dropbox Client] Component mounted, starting initial connection test...');
    testConnectionMutation.mutate();
  }, []);

  const refreshTokenMutation = useMutation({
    mutationFn: async () => {
      const response = await authedFetch('/api/dropbox/refresh-token', {
        method: 'POST',
      });
      if (!response.ok) throw new Error('Token refresh failed');
      return response.json();
    },
    onSuccess: (data) => {
      toast({
        title: "Token Refreshed",
        description: "Dropbox access token has been refreshed successfully",
      });
      // Re-test connection after refresh
      testConnectionMutation.mutate();
    },
    onError: (error) => {
      toast({
        title: "Refresh Failed",
        description: "Unable to refresh Dropbox token",
        variant: "destructive",
      });
    },
  });

  const generateOAuthUrlMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch('/api/dropbox/oauth-url-direct');
      if (!response.ok) throw new Error('Failed to generate OAuth URL');
      return response.json();
    },
    onSuccess: (data) => {
      setOauthUrl(data.oauthUrl);
      setShowTokenGeneration(true);
      toast({
        title: "OAuth URL Generated",
        description: "Click the link to authorize the app with Dropbox",
      });
    },
    onError: (error) => {
      toast({
        title: "Failed to Generate URL",
        description: "Unable to create OAuth authorization URL",
        variant: "destructive",
      });
    },
  });

  const exchangeCodeMutation = useMutation({
    mutationFn: async (code: string) => {
      const response = await authedFetch('/api/dropbox/exchange-code-direct', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Code exchange failed');
      }
      return response.json();
    },
    onSuccess: (data) => {
      if (data.success) {
        toast({
          title: "Connected!",
          description: "Dropbox credentials saved to database successfully.",
        });
        setAuthCode('');
        setShowTokenGeneration(false);
        refetchCredentialsInfo();
        testConnectionMutation.mutate();
      } else if (data.refreshToken) {
        setGeneratedToken(data.refreshToken);
        navigator.clipboard.writeText(data.refreshToken);
        toast({
          title: "New Refresh Token Generated!",
          description: "The refresh token has been copied to your clipboard.",
        });
      }
    },
    onError: (error) => {
      toast({
        title: "Code Exchange Failed",
        description: error instanceof Error ? error.message : "Unable to exchange authorization code for tokens",
        variant: "destructive",
      });
    },
  });

  // Manual token save
  const saveManualCredentials = async () => {
    if (!manualRefreshToken.trim()) {
      toast({
        title: "Refresh Token Required",
        description: "Please enter the refresh token",
        variant: "destructive",
      });
      return;
    }
    
    setIsSavingCredentials(true);
    try {
      const response = await apiRequest('POST', '/api/dropbox/manual-token', {
        refreshToken: manualRefreshToken.trim(),
        appKey: manualAppKey.trim() || undefined,
        appSecret: manualAppSecret.trim() || undefined
      });
      const data = await response.json();
      
      if (data.success) {
        toast({
          title: "Credentials Saved",
          description: data.message || "Dropbox credentials validated and saved.",
        });
        setManualAppKey('');
        setManualAppSecret('');
        setManualRefreshToken('');
        setShowManualEntry(false);
        refetchCredentialsInfo();
        testConnectionMutation.mutate();
      } else {
        throw new Error(data.error || 'Save failed');
      }
    } catch (error: any) {
      toast({
        title: "Save Failed",
        description: error.message || "Could not save credentials",
        variant: "destructive",
      });
    } finally {
      setIsSavingCredentials(false);
    }
  };

  const getStatusColor = () => {
    if (testError) return "secondary";
    if (!status) return "secondary";
    if (status.connected && status.hasValidToken) return "default";
    if (status.connected && !status.hasValidToken) return "destructive";
    return "secondary";
  };

  const getStatusText = () => {
    if (testError) return "Not verified";
    if (!status) return "Not tested";
    if (status.connected && status.hasValidToken) return "Connected";
    if (status.connected && !status.hasValidToken) return "Token expired";
    return "Disconnected";
  };

  const getStatusIcon = () => {
    if (testConnectionMutation.isPending) return <Loader2 className="h-4 w-4 animate-spin" />;
    if (testError) return <AlertTriangle className="h-4 w-4" />;
    if (!status) return <Cloud className="h-4 w-4" />;
    if (status.connected && status.hasValidToken) return <CheckCircle className="h-4 w-4" />;
    return <XCircle className="h-4 w-4" />;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-white">Dropbox Storage</h3>
        <Badge variant={getStatusColor()} className="flex items-center gap-2">
          {getStatusIcon()}
          {getStatusText()}
        </Badge>
      </div>

      {testError && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Could not run the Dropbox check: {testError}. This does not mean the Dropbox connection is broken.
          </AlertDescription>
        </Alert>
      )}

      {/* Connection Status Details */}
      {status && (
        <div className="grid grid-cols-2 gap-4 p-4 navy-bg border border-gray-600 rounded-lg">
          <div>
            <div className="text-sm font-medium text-gray-300">Connection Status</div>
            <div className="text-sm text-gray-400">
              {status.connected ? "✅ Connected" : "❌ Not connected"}
            </div>
          </div>
          
          <div>
            <div className="text-sm font-medium text-gray-300">Access Token</div>
            <div className="text-sm text-gray-400">
              {status.hasValidToken ? "✅ Valid" : "❌ Invalid/Expired"}
            </div>
          </div>
          
          <div>
            <div className="text-sm font-medium text-gray-300">Refresh Available</div>
            <div className="text-sm text-gray-400">
              {status.canRefreshToken ? "✅ Yes" : "❌ No"}
            </div>
          </div>
          
          {status.folderPath && (
            <div>
              <div className="text-sm font-medium text-gray-300">App Folder</div>
              <div className="text-sm text-gray-400 font-mono">{status.folderPath}</div>
            </div>
          )}
          
          {status.lastRefresh && (
            <div className="col-span-2">
              <div className="text-sm font-medium text-gray-300">Last Token Refresh</div>
              <div className="text-sm text-gray-400">{new Date(status.lastRefresh).toLocaleString()}</div>
            </div>
          )}
          
          {status.error && (
            <div className="col-span-2">
              <div className="text-sm font-medium text-red-400">Error Details</div>
              <div className="text-sm text-red-300 font-mono bg-red-900/20 p-2 rounded border">
                {status.error}
              </div>
            </div>
          )}

          {status.diagnosis && (
            <div className="col-span-2">
              <div className="text-sm font-medium text-yellow-400">Diagnosis</div>
              <div className="text-sm text-yellow-300 bg-yellow-900/20 p-2 rounded border">
                {status.diagnosis}
              </div>
            </div>
          )}

          {status.recommendation && (
            <div className="col-span-2">
              <div className="text-sm font-medium text-blue-400">Recommendation</div>
              <div className="text-sm text-blue-300 bg-blue-900/20 p-2 rounded border">
                {status.recommendation}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Credentials Source Info */}
      {credentialsInfo && (
        <div className="p-4 navy-bg border border-gray-600 rounded-lg space-y-3">
          <div className="text-sm font-medium text-gray-300">Credential Sources</div>
          <div className="grid grid-cols-2 gap-4 text-xs">
            <div className="space-y-2">
              <div className="font-medium text-gray-400">Database (Primary)</div>
              <div className="flex justify-between">
                <span className="text-gray-500">Refresh Token:</span>
                <span className={credentialsInfo.database.hasRefreshToken ? 'text-green-400' : 'text-red-400'}>
                  {credentialsInfo.database.hasRefreshToken ? (
                    <span className="flex items-center gap-1">
                      {credentialsInfo.database.refreshTokenPreview}
                      <CheckCircle className="h-3 w-3" />
                    </span>
                  ) : (
                    <span className="flex items-center gap-1">
                      Missing
                      <XCircle className="h-3 w-3" />
                    </span>
                  )}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">App Key:</span>
                <span className={credentialsInfo.database.hasAppKey ? 'text-green-400' : 'text-yellow-400'}>
                  {credentialsInfo.database.hasAppKey ? credentialsInfo.database.appKeyPreview : 'Not set'}
                </span>
              </div>
              {credentialsInfo.database.lastRefresh && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Last Refresh:</span>
                  <span className="text-gray-300">
                    {formatDistanceToNow(new Date(credentialsInfo.database.lastRefresh), { addSuffix: true })}
                  </span>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <div className="font-medium text-gray-400">Environment (Fallback)</div>
              <div className="flex justify-between">
                <span className="text-gray-500">Refresh Token:</span>
                <span className={credentialsInfo.environment.hasRefreshToken ? 'text-green-400' : 'text-yellow-400'}>
                  {credentialsInfo.environment.hasRefreshToken ? (
                    <span className="flex items-center gap-1">
                      Available
                      <CheckCircle className="h-3 w-3" />
                    </span>
                  ) : (
                    <span className="flex items-center gap-1">
                      Not Set
                      <AlertTriangle className="h-3 w-3" />
                    </span>
                  )}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">App Key:</span>
                <span className={credentialsInfo.environment.hasAppKey ? 'text-green-400' : 'text-yellow-400'}>
                  {credentialsInfo.environment.hasAppKey ? credentialsInfo.environment.appKeyPreview : 'Not set'}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-3">
        <Button
          onClick={() => {
            setIsManualTest(true);
            testConnectionMutation.mutate();
          }}
          disabled={testConnectionMutation.isPending}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          {testConnectionMutation.isPending ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Testing...
            </>
          ) : (
            <>
              <Cloud className="h-4 w-4 mr-2" />
              Test Connection
            </>
          )}
        </Button>

        {status && !status.hasValidToken && status.canRefreshToken && (
          <Button
            onClick={() => refreshTokenMutation.mutate()}
            disabled={refreshTokenMutation.isPending}
            variant="outline"
            className="border-orange-600 text-orange-400 hover:bg-orange-600/10"
          >
            {refreshTokenMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Refreshing...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh Token
              </>
            )}
          </Button>
        )}

        {/* Always show Connect button when not connected or no valid token */}
        {(!status?.connected || !status?.hasValidToken) && (
          <Button
            onClick={() => generateOAuthUrlMutation.mutate()}
            disabled={generateOAuthUrlMutation.isPending}
            className="bg-green-600 hover:bg-green-700 text-white"
            data-testid="button-connect-dropbox"
          >
            {generateOAuthUrlMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Cloud className="h-4 w-4 mr-2" />
                Connect with Dropbox
              </>
            )}
          </Button>
        )}
      </div>

      {/* Token Generation Interface */}
      {showTokenGeneration && (
        <div className="p-4 bg-red-900/20 border border-red-600 rounded-lg">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="h-5 w-5 text-red-400" />
            <h4 className="font-medium text-red-400">Generate New Refresh Token</h4>
          </div>
          
          <div className="space-y-3">
            <div>
              <div className="text-sm font-medium text-gray-300 mb-2">Step 1: Authorize with Dropbox</div>
              <div className="flex gap-2">
                <Button
                  onClick={() => window.open(oauthUrl, '_blank')}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Open Dropbox Authorization
                </Button>
                <Button
                  onClick={() => {
                    navigator.clipboard.writeText(oauthUrl);
                    toast({ title: "URL Copied", description: "OAuth URL copied to clipboard" });
                  }}
                  variant="outline"
                  className="border-gray-600 text-gray-400"
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Copy URL
                </Button>
              </div>
            </div>

            <div>
              <div className="text-sm font-medium text-gray-300 mb-2">Step 2: Enter Authorization Code</div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={authCode}
                  onChange={(e) => setAuthCode(e.target.value)}
                  placeholder="Paste authorization code here..."
                  className="flex-1 px-3 py-2 bg-gray-800 border border-gray-600 rounded text-white placeholder-gray-400"
                />
                <Button
                  onClick={() => {
                    if (authCode.trim()) {
                      exchangeCodeMutation.mutate(authCode.trim());
                    }
                  }}
                  disabled={!authCode.trim() || exchangeCodeMutation.isPending}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  {exchangeCodeMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    "Exchange"
                  )}
                </Button>
              </div>
            </div>

            <div className="text-xs text-gray-400">
              After clicking "Exchange", the new refresh token will be copied to your clipboard. 
              Update your DROPBOX_REFRESH_TOKEN secret with this value.
            </div>

            {/* Display generated token */}
            {generatedToken && (
              <Alert className="bg-green-900/20 border-green-600">
                <CheckCircle className="h-4 w-4 text-green-400" />
                <AlertDescription className="text-green-300">
                  <div className="font-medium mb-2">Success! Your new refresh token:</div>
                  <div className="bg-gray-900 p-2 rounded font-mono text-xs break-all">
                    {generatedToken}
                  </div>
                  <div className="mt-2 flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        navigator.clipboard.writeText(generatedToken);
                        toast({ title: "Copied!", description: "Refresh token copied to clipboard" });
                      }}
                      className="text-green-400 border-green-600 hover:bg-green-600/10"
                    >
                      <Copy className="h-3 w-3 mr-1" />
                      Copy Token
                    </Button>
                    <span className="text-xs text-gray-400 py-1">
                      Update your DROPBOX_REFRESH_TOKEN secret with this value
                    </span>
                  </div>
                </AlertDescription>
              </Alert>
            )}
          </div>
        </div>
      )}

      {/* Manual Token Entry */}
      <Separator className="bg-gray-700" />
      
      <Collapsible open={showManualEntry} onOpenChange={setShowManualEntry}>
        <CollapsibleTrigger asChild>
          <Button
            variant="outline"
            className="w-full justify-between border-amber-600/50 bg-amber-900/20 text-amber-400 hover:text-amber-300 hover:bg-amber-900/40 hover:border-amber-500"
            data-testid="button-toggle-manual-entry"
          >
            <div className="flex items-center gap-2">
              <Key className="h-4 w-4" />
              <span className="font-medium">Advanced: Manual Token Entry</span>
            </div>
            {showManualEntry ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-4 pt-4">
          <div className="bg-yellow-900/20 border border-yellow-700/50 rounded-lg p-3">
            <p className="text-xs text-yellow-400">
              Use this if OAuth fails (e.g., URL whitelist issues) or when setting up a new Dropbox App.
              Leave App Key/Secret empty to use existing values.
            </p>
          </div>
          
          <div className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="manualAppKey" className="text-gray-300 text-sm">
                App Key <span className="text-gray-500">(optional - uses existing if empty)</span>
              </Label>
              <Input
                id="manualAppKey"
                value={manualAppKey}
                onChange={(e) => setManualAppKey(e.target.value)}
                placeholder={credentialsInfo?.database.appKeyPreview || "Enter App Key..."}
                className="bg-gray-800 border-gray-600 text-white font-mono text-sm"
                data-testid="input-manual-app-key"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="manualAppSecret" className="text-gray-300 text-sm">
                App Secret <span className="text-gray-500">(optional - uses existing if empty)</span>
              </Label>
              <div className="relative">
                <Input
                  id="manualAppSecret"
                  type={showAppSecret ? "text" : "password"}
                  value={manualAppSecret}
                  onChange={(e) => setManualAppSecret(e.target.value)}
                  placeholder="Enter App Secret..."
                  className="bg-gray-800 border-gray-600 text-white font-mono text-sm pr-10"
                  data-testid="input-manual-app-secret"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0 text-gray-400 hover:text-white"
                  onClick={() => setShowAppSecret(!showAppSecret)}
                >
                  {showAppSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="manualRefreshToken" className="text-gray-300 text-sm">
                Refresh Token <span className="text-red-400">*</span>
              </Label>
              <div className="relative">
                <Input
                  id="manualRefreshToken"
                  type={showRefreshTokenField ? "text" : "password"}
                  value={manualRefreshToken}
                  onChange={(e) => setManualRefreshToken(e.target.value)}
                  placeholder="Enter Refresh Token..."
                  className="bg-gray-800 border-gray-600 text-white font-mono text-sm pr-10"
                  data-testid="input-manual-refresh-token"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0 text-gray-400 hover:text-white"
                  onClick={() => setShowRefreshTokenField(!showRefreshTokenField)}
                >
                  {showRefreshTokenField ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              onClick={saveManualCredentials}
              disabled={!manualRefreshToken.trim() || isSavingCredentials}
              className="bg-green-600 hover:bg-green-700"
              data-testid="button-save-manual-credentials"
            >
              {isSavingCredentials ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="h-4 w-4 mr-2" />
              )}
              Validate & Save Credentials
            </Button>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Information */}
      <div className="text-sm text-gray-400 p-4 bg-gray-800/50 border border-gray-700 rounded-lg">
        <div className="font-medium text-gray-300 mb-2">Dropbox Integration</div>
        <ul className="space-y-1 text-xs">
          <li>• Email attachments are automatically uploaded to Dropbox</li>
          <li>• Files are organized by year, project, and pulse number</li>
          <li>• Shared links are created for direct download access</li>
          <li>• Token refresh happens automatically when needed</li>
          <li>• Refresh tokens never expire unless manually revoked</li>
          <li>• Connection issues are handled with automatic fallback strategies</li>
        </ul>
      </div>
    </div>
  );
}